import { useMutation } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { type AxiosError, automaticImportApi } from "libs/api";
import {
    type ExtendedScrapingErrorResponse,
    PropertySelectionItemExternalPropertyTypeEnum,
    PropertySelectionItemStatusEnum,
} from "libs/codegen/property-onboarding";
import { useNavigate, useParams } from "router";
import { clearCampaignParams, getCampaignParams } from "../campaignParams.ts";
import { scrapingErrorResponseMapper } from "../urlInput/scrapingErrorResponseMapper.ts";
import { useSetProperties } from "./propertiesAtom.ts";
import { selectablePropertiesFilter } from "./selectablePropertiesFilter.ts";
import { useSetSelectedProperties } from "./selectedPropertiesAtom.ts";

export const usePropertyListing = () => {
    const navigate = useNavigate();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic/lists");
    const [searchParams] = useSearchParams();
    const campaignParams = getCampaignParams(searchParams);

    const setProperties = useSetProperties();
    const setSelectedProperties = useSetSelectedProperties();

    const handleScrapingErrorResponse = (e: AxiosError<ExtendedScrapingErrorResponse>) => {
        if (typeof e?.data?.errorType === "number") {
            navigate("/:lang/list-my-property/get-started/automatic", {
                params: {
                    lang,
                },
                state: {
                    errorResponse: e?.data,
                },
            });
        } else {
            navigate("/:lang/list-my-property/get-started/automatic", {
                params: {
                    lang,
                },
                state: {
                    errorResponse: {
                        errorType: scrapingErrorResponseMapper.UnexpectedError,
                        propertyItem: null,
                        message: null,
                    } satisfies ExtendedScrapingErrorResponse,
                },
            });
        }
    };

    const { isLoading, mutate, isIdle } = useMutation({
        mutationFn: (sanitizedUrl: string) => automaticImportApi.getPropertyListing(lang, sanitizedUrl),
        onSuccess: async ({ data: properties }) => {
            if (
                properties.length === 1 &&
                selectablePropertiesFilter(properties[0]) &&
                properties[0].externalPropertyType !== PropertySelectionItemExternalPropertyTypeEnum.NUMBER_3
            ) {
                try {
                    const { data } = await automaticImportApi.importProperty(lang, {
                        selected: properties.filter(selectablePropertiesFilter).map((o) => ({
                            ...o,
                            campaignId: campaignParams.ocid,
                            encryptedEmail: campaignParams.e,
                        })),
                        unselected: [],
                    });

                    clearCampaignParams();
                    window.location.assign(data.redirectTo);
                } catch (e) {
                    handleScrapingErrorResponse(e as AxiosError<ExtendedScrapingErrorResponse>);
                }
            } else if (properties.length === 1 && properties[0].status === PropertySelectionItemStatusEnum.NUMBER_4) {
                const onboardingId = properties[0].id;
                if (!onboardingId) {
                    handleScrapingErrorResponse({
                        data: {
                            errorType: scrapingErrorResponseMapper.UnexpectedError,
                            propertyItem: null,
                            message: null,
                        },
                    } as AxiosError<ExtendedScrapingErrorResponse>);
                    return;
                }
                window.location.assign(`/${lang}/list-my-property/onboard/${onboardingId}`);
            } else if (
                properties.length === 1 &&
                (properties[0].status === PropertySelectionItemStatusEnum.NUMBER_1 ||
                    properties[0].status === PropertySelectionItemStatusEnum.NUMBER_0)
            ) {
                navigate("/:lang/list-my-property/get-started/automatic", {
                    params: { lang },
                    state: {
                        errorResponse: {
                            errorType: scrapingErrorResponseMapper.Duplicated,
                            propertyItem: properties[0],
                            message: null,
                        } satisfies ExtendedScrapingErrorResponse,
                    },
                });
            } else {
                // force delay at least 2 seconds per design request
                await new Promise((r) => setTimeout(r, 2000));

                setProperties(properties);
                setSelectedProperties(properties.filter(selectablePropertiesFilter).map((p) => p.id));
            }
        },
        onError: handleScrapingErrorResponse,
    });

    return {
        isLoading,
        isIdle,
        mutate,
    };
};
